import { useCallback, useMemo } from 'react';
import useLocalStorage from 'react-localstorage-hook';

import { FarmCard } from '@src/types/vaults/farm.types';
import { simulateContract, waitForTransactionReceipt } from '@wagmi/core';
import { Address } from 'viem';
import { useReadContract, useReadContracts, useSimulateContract } from 'wagmi';

import {
  useUpdateVaultData,
  useUpdateVaultFarms,
} from '@services/api/vaults/vaults.api';

import { useAuth } from '@contexts/common/AuthorizationContext';

import { config } from '@tools/wagmi';

import { mapWagmiError } from '@helpers/common/error.helpers';

import { useWriteContractExtraGas } from '@utils/contract.utils';
import { applyDecimals, toBN } from '@utils/formaters.utils';
import { isAddressesEqual } from '@utils/tokens.utils';

import showToast from '@components/common/Toast';

import { PERCENT_DIVIDER } from '@constants/common/common.constants';
import { SLIPPAGE_TOLERANCE } from '@constants/common/localStorage.constants';
import { SuccessMessages } from '@constants/common/messages.constants';
import { DEFAULT_SLIPPAGE_PARAM } from '@constants/common/slippage.constants';
import { ToastType } from '@constants/common/toast.constants';
import { DEFAULT_VAULT_DIVIDER } from '@constants/vaults/common.constants';

import abi from '@abi/vault/Vault';

import { TokensDto } from '@dto/vaults/tokens.dto';
import { LegacyVaultsDto, VaultDto } from '@dto/vaults/vaults.dto';

import { VaultFarmsType } from '@app-types/vaults/vault.types';

export const useHarvestInfo = ({
  vault,
  tokens,
}: {
  vault: LegacyVaultsDto;
  tokens: TokensDto | undefined;
}) => {
  const { data: native, isLoading: isLoadingData } = useReadContract({
    address: vault.address as Address,
    abi,
    functionName: 'native',
    chainId: vault.chainId,
  });

  const {
    data: harvest,
    refetch,
    isLoading: isLoadingSimulateContract,
  } = useSimulateContract({
    address: vault.address as Address,
    abi,
    functionName: 'harvest',
    args: [BigInt(0)],
    chainId: vault.chainId,
  });

  const harvestInfo = useMemo(() => {
    if (
      !vault ||
      !tokens ||
      !native ||
      !harvest?.result ||
      !vault.accountBalance ||
      !vault.totalSupply
    ) {
      return { harvestAmountUsd: toBN(0), profitAmountUsd: toBN(0) };
    }

    const profitAmount = toBN(harvest.result[2]);
    const reinvestAmount = toBN(harvest.result[0]);

    const harvestSum = reinvestAmount.plus(profitAmount);
    const harvestAmount = harvestSum
      .times(vault.accountBalance || 0)
      .div(vault.totalSupply);
    const nativeInUsd =
      tokens.farm[vault.chainId][native.toLowerCase()] ||
      tokens.common[vault.chainId][native.toLowerCase()];

    if (!nativeInUsd.priceInUsd) {
      return { harvestAmountUsd: toBN(0), profitAmountUsd: toBN(0) };
    }
    const harvestAmountUsd = toBN(harvestAmount, DEFAULT_VAULT_DIVIDER).times(
      nativeInUsd.priceInUsd,
    );
    const accountProfitAmount = toBN(profitAmount)
      .times(vault.accountBalance || 0)
      .div(vault.totalSupply);
    const accountProfitAmountUsd = toBN(
      accountProfitAmount,
      DEFAULT_VAULT_DIVIDER,
    ).times(nativeInUsd.priceInUsd);

    if (harvestAmountUsd.isNaN() || accountProfitAmountUsd.isNaN()) {
      return { harvestAmountUsd: toBN(0), profitAmountUsd: toBN(0) };
    }

    return { harvestAmountUsd, profitAmountUsd: accountProfitAmountUsd };
  }, [native, harvest, vault, tokens]);

  return {
    harvestInfo,
    refetch,
    isLoading: isLoadingData || isLoadingSimulateContract,
  };
};

export const useHarvest = (
  vault: VaultDto,
  handleUpdate?: () => Promise<void>,
) => {
  const { chainId, account } = useAuth();
  const { mutateAsync: updateData } = useUpdateVaultData();

  const { writeContract, ...rest } = useWriteContractExtraGas({
    config,
    mutation: {
      onSuccess: async (data) => {
        const transactionReceipt = await waitForTransactionReceipt(config, {
          hash: data,
        });

        await updateData({ account: account as Address, vaultId: vault.id });
        if (handleUpdate) await handleUpdate();

        showToast(
          {
            title: SuccessMessages.Harvested,
            withLink: {
              hash: transactionReceipt.transactionHash,
              chainId: chainId as number,
            },
          },
          ToastType.Success,
        );
      },
      onError: (e) => {
        console.log('useHarvest:', e);
        showToast({ title: mapWagmiError(e) }, ToastType.Error);
      },
    },
  });

  const onWriteContract = useCallback(async () => {
    const minAmountOutRequest = await simulateContract(config, {
      abi,
      address: vault.address as Address,
      functionName: 'harvest',
      chainId,
      args: [BigInt(0)],
      account: account as Address,
    });

    const minAmountOut = toBN(minAmountOutRequest.result[5])
      .times(DEFAULT_SLIPPAGE_PARAM)
      .toFixed(0);

    writeContract({
      abi,
      address: vault.address as Address,
      functionName: 'harvest',
      chainId,
      args: [BigInt(minAmountOut)],
    });
  }, [chainId, writeContract, account, vault]);

  return {
    writeContract: onWriteContract,
    ...rest,
  };
};

export const useEmergencyWithdraw = (
  vault: LegacyVaultsDto,
  handleUpdate: () => Promise<void>,
) => {
  const { chainId, account } = useAuth();
  const { mutateAsync: updateData } = useUpdateVaultData();

  const { writeContract, ...rest } = useWriteContractExtraGas({
    config,
    mutation: {
      onSuccess: async (data) => {
        const transactionReceipt = await waitForTransactionReceipt(config, {
          hash: data,
        });

        // await updateData({ account: account as Address, vaultId: vault.id });
        await handleUpdate();

        showToast(
          {
            title: SuccessMessages.Withdraw,
            withLink: {
              hash: transactionReceipt.transactionHash,
              chainId: chainId as number,
            },
          },
          ToastType.Success,
        );
      },
      onError: (e) => {
        console.log('useEmergencyWithdraw:', e);
        showToast({ title: mapWagmiError(e) }, ToastType.Error);
      },
    },
  });

  const onWriteContract = useCallback(() => {
    writeContract({
      abi,
      address: vault.address as Address,
      functionName: 'emergencyWithdraw',
      chainId,
      args: [],
      account,
    });
  }, [chainId, account, vault]);

  return {
    writeContract: onWriteContract,
    ...rest,
  };
};

export const usePublish = (
  vault: VaultDto,
  handleUpdate: () => Promise<void>,
) => {
  const { chainId, account } = useAuth();
  const { mutateAsync: updateData } = useUpdateVaultData();

  const { writeContract, ...rest } = useWriteContractExtraGas({
    config,
    mutation: {
      onSuccess: async (data) => {
        const transactionReceipt = await waitForTransactionReceipt(config, {
          hash: data,
        });

        await updateData({ account: account as Address, vaultId: vault.id });
        await handleUpdate();

        showToast(
          {
            title: SuccessMessages.Published,
            withLink: {
              hash: transactionReceipt.transactionHash,
              chainId: chainId as number,
            },
          },
          ToastType.Success,
        );
      },
      onError: (e) => {
        console.log('usePublish:', e);
        showToast({ title: mapWagmiError(e) }, ToastType.Error);
      },
    },
  });

  const onWriteContract = useCallback(async () => {
    const minAmountOutRequest = await simulateContract(config, {
      abi,
      address: vault.address as Address,
      functionName: 'publish',
      chainId,
      args: [BigInt(0)],
      account: account as Address,
    });

    const minAmountOut = toBN(minAmountOutRequest.result)
      .times(DEFAULT_SLIPPAGE_PARAM)
      .toFixed(0);

    writeContract({
      abi,
      address: vault.address as Address,
      functionName: 'publish',
      chainId,
      args: [BigInt(minAmountOut)],
    });
  }, [chainId, writeContract, account, vault]);

  return {
    writeContract: onWriteContract,
    ...rest,
  };
};

type LpEstimationDepositParamType = {
  vault: VaultDto;
  currentFarm: VaultFarmsType;
  vaultFarms: VaultFarmsType[];
  amountIn: string;
};

export const useLpEstimationDeposit = ({
  vault,
  currentFarm,
  vaultFarms,
  amountIn,
}: LpEstimationDepositParamType) => {
  const { data: depositInfo } = useReadContracts({
    contracts: [
      {
        address: vault.address as Address,
        abi,
        functionName: 'farmsCount',
        chainId: vault.chainId,
      },
      {
        address: vault.address as Address,
        abi,
        functionName: 'convertlessDepositRatio',
        chainId: vault.chainId,
      },
    ],
    allowFailure: false,
  });

  const farmsCalls = useMemo(() => {
    if (!depositInfo) return [];
    const farmsCount = depositInfo[0];
    const indexesFarmsCount = Array.from(Array(Number(farmsCount)).keys());

    return indexesFarmsCount.map((item) => ({
      address: vault.address as Address,
      abi,
      functionName: 'farms' as const,
      chainId: vault.chainId,
      args: [item],
    }));
  }, [depositInfo, vault]);

  const { data: farms } = useReadContracts({
    contracts: farmsCalls,
    allowFailure: false,
    query: {
      enabled: !!farmsCalls.length,
    },
  });

  return useMemo(() => {
    if (!farms || !currentFarm || !depositInfo) return [];

    const farmsIndex = farms.findIndex((item) =>
      isAddressesEqual(item.beacon, currentFarm.farmInfo.beacon),
    );
    const amounts = depositInfo[1];

    const amount = applyDecimals(
      amountIn,
      currentFarm.farmInfo.stakingToken.decimals,
    );
    const depositCof = toBN(amount).div(toBN(amounts[farmsIndex]));

    return farms.map((farm, index) => {
      const { beacon } = farm;
      const _farm = vaultFarms.find((item) =>
        isAddressesEqual(item.farmInfo.beacon, beacon),
      );
      const farmId = _farm?.farm.farmId as number;

      return {
        farmId,
        amount: isAddressesEqual(beacon, currentFarm.farmInfo.beacon)
          ? toBN(amount)
          : toBN(amounts[index]).times(depositCof),
      };
    });
  }, [farms, currentFarm, amountIn, vaultFarms, depositInfo]);
};

type DepositConvertlessParamType = {
  vault: VaultDto;
  handleUpdate: () => Promise<void>;
};

export const useDepositConvertless = ({
  vault,
  handleUpdate,
}: DepositConvertlessParamType) => {
  const { chainId, account } = useAuth();
  const { mutateAsync: updateData } = useUpdateVaultData();
  const [slippageInStorage] = useLocalStorage(SLIPPAGE_TOLERANCE);

  const { writeContract, ...rest } = useWriteContractExtraGas({
    config,
    mutation: {
      onSuccess: async (data) => {
        const transactionReceipt = await waitForTransactionReceipt(config, {
          hash: data,
        });

        await updateData({ account: account as Address, vaultId: vault.id });
        await handleUpdate();

        showToast(
          {
            title: SuccessMessages.Deposited,
            withLink: {
              hash: transactionReceipt.transactionHash,
              chainId: chainId as number,
            },
          },
          ToastType.Success,
        );
      },
      onError: (e) => {
        console.log('useDepositConvertless:', e);
        showToast({ title: mapWagmiError(e) }, ToastType.Error);
      },
    },
  });

  const onWriteContract = useCallback(
    async (depositData: bigint[]) => {
      const minAmountOutRequest = await simulateContract(config, {
        abi,
        address: vault.address as Address,
        functionName: 'depositConvertless',
        chainId: vault.chainId,
        args: [depositData, BigInt('1')],
        account: account as Address,
      });

      const minAmountOut = toBN(minAmountOutRequest.result)
        .times(slippageInStorage as string)
        .toFixed(0);

      writeContract({
        abi,
        address: vault.address as Address,
        functionName: 'depositConvertless',
        chainId: vault.chainId,
        args: [depositData, BigInt(minAmountOut)],
      });
    },
    [writeContract, account, vault, slippageInStorage],
  );

  return {
    writeContract: onWriteContract,
    ...rest,
  };
};

type WithdrawConvertlessInfoParamType = {
  vault: VaultDto;
  vaultFarms: VaultFarmsType[];
  amountIn: string;
};

export const useWithdrawConvertlessInfo = ({
  amountIn,
  vault,
  vaultFarms,
}: WithdrawConvertlessInfoParamType) => {
  const { account } = useAuth();

  const amount = useMemo(
    () => applyDecimals(amountIn, DEFAULT_VAULT_DIVIDER),
    [amountIn],
  );

  const { data, isPending: isPendingData } = useSimulateContract({
    abi,
    address: vault.address as Address,
    functionName: 'withdrawConvertless',
    args: [BigInt(amount.toFixed()), BigInt(0)],
    chainId: vault.chainId,
    account,
    query: {
      enabled: toBN(amountIn).gt(0),
    },
  });

  const { data: farmsCount, isPending: isPendingFarmsCount } = useReadContract({
    abi,
    address: vault.address as Address,
    functionName: 'farmsCount',
    chainId: vault.chainId,
    account,
  });

  const farmsCalls = useMemo(() => {
    if (!farmsCount) return [];

    const indexesFarmsCount = Array.from(Array(Number(farmsCount)).keys());

    return indexesFarmsCount.map((item) => ({
      address: vault.address as Address,
      abi,
      functionName: 'farms' as const,
      chainId: vault.chainId,
      args: [item],
    }));
  }, [farmsCount, vault]);

  const { data: farms, isPending: isPendingFarms } = useReadContracts({
    contracts: farmsCalls,
    allowFailure: false,
    query: {
      enabled: !!farmsCalls.length,
    },
  });

  const farmsResult = useMemo(() => {
    if (!farms || !data || !vaultFarms) return [];

    return farms.map((farm, index) => ({
      farm: vaultFarms.find((item) =>
        isAddressesEqual(item.farmInfo.beacon, farm.beacon),
      ) as VaultFarmsType,
      amount: data.result[0][index],
    }));
  }, [farms, data, vaultFarms]);

  const isPending = useMemo(
    () => isPendingFarms || isPendingFarmsCount || isPendingData,
    [isPendingFarms, isPendingFarmsCount, isPendingData],
  );

  return {
    data: farmsResult,
    isPending,
  };
};

export const useWithdrawConvertless = ({
  vault,
  handleUpdate,
}: DepositConvertlessParamType) => {
  const { chainId, account } = useAuth();
  const { mutateAsync: updateData } = useUpdateVaultData();
  const [slippageInStorage] = useLocalStorage(SLIPPAGE_TOLERANCE);

  const { writeContract, ...rest } = useWriteContractExtraGas({
    config,
    mutation: {
      onSuccess: async (data) => {
        const transactionReceipt = await waitForTransactionReceipt(config, {
          hash: data,
        });

        await updateData({ account: account as Address, vaultId: vault.id });
        await handleUpdate();

        showToast(
          {
            title: SuccessMessages.Withdraw,
            withLink: {
              hash: transactionReceipt.transactionHash,
              chainId: chainId as number,
            },
          },
          ToastType.Success,
        );
      },
      onError: (e) => {
        console.log('useWithdrawConvertless:', e);
        showToast({ title: mapWagmiError(e) }, ToastType.Error);
      },
    },
  });

  const onWriteContract = useCallback(
    async (amountIn: bigint) => {
      const minAmountOutRequest = await simulateContract(config, {
        abi,
        address: vault.address as Address,
        functionName: 'withdrawConvertless',
        chainId: vault.chainId,
        args: [amountIn, BigInt('0')],
        account: account as Address,
      });

      const minAmountOut = toBN(minAmountOutRequest.result[1])
        .times(slippageInStorage as string)
        .toFixed(0);

      writeContract({
        abi,
        address: vault.address as Address,
        functionName: 'withdrawConvertless',
        chainId: vault.chainId,
        args: [amountIn, BigInt(minAmountOut)],
      });
    },
    [writeContract, account, vault, slippageInStorage],
  );

  return {
    writeContract: onWriteContract,
    ...rest,
  };
};

export const useVaultDetails = ({
  vault,
  isFullInfo = false,
}: {
  vault: LegacyVaultsDto;
  isFullInfo?: boolean;
}) => {
  const { data, isPending, refetch } = useReadContract({
    abi,
    address: vault.address as Address,
    functionName: 'totalSupply',
    chainId: vault.chainId,
    query: {
      enabled: isFullInfo,
    },
  });

  const onUpdate = useCallback(async () => {
    await refetch();
  }, []);

  return {
    totalSupply: toBN(data || 0),
    isPending,
    refetch: onUpdate,
  };
};

export const useVaultUserBalance = ({
  vault,
  isFullInfo = false,
}: {
  vault: LegacyVaultsDto;
  isFullInfo?: boolean;
}) => {
  const { account } = useAuth();
  const { data, isPending, refetch } = useReadContract({
    abi,
    address: vault.address as Address,
    functionName: 'balanceOf',
    chainId: vault.chainId,
    args: [account as Address],
    query: {
      enabled: isFullInfo,
    },
  });

  const onUpdate = useCallback(async () => {
    await refetch();
  }, []);

  return {
    userBalance: toBN(data || 0),
    isPending,
    refetch: onUpdate,
  };
};

export const useEditFarms = (
  vault: VaultDto,
  handleUpdate: () => Promise<void>,
) => {
  const { chainId, account } = useAuth();

  const { mutateAsync: updateVaultAction } = useUpdateVaultFarms();

  const { writeContract, ...rest } = useWriteContractExtraGas({
    config,
    mutation: {
      onSuccess: async (data) => {
        const transactionReceipt = await waitForTransactionReceipt(config, {
          hash: data,
        });

        await updateVaultAction({ vaultId: vault.id });
        await handleUpdate();

        showToast(
          {
            title: SuccessMessages.Updated,
            withLink: {
              hash: transactionReceipt.transactionHash,
              chainId: chainId as number,
            },
          },
          ToastType.Success,
        );
      },
      onError: (e) => {
        console.log('useEditFarms:', e);
        showToast({ title: mapWagmiError(e) }, ToastType.Error);
      },
    },
  });

  const onWriteContract = useCallback(
    async (farms: FarmCard[]) => {
      const farmData = farms.map((item) => ({
        beacon: item.farm.beacon as Address,
        percent: BigInt(
          applyDecimals(item.percent, DEFAULT_VAULT_DIVIDER)
            .div(PERCENT_DIVIDER)
            .toFixed(0),
        ),
        data: '0x' as Address,
      }));

      const minAmountOutRequest = await simulateContract(config, {
        abi,
        address: vault.address as Address,
        functionName: 'updateFarms',
        chainId,
        args: [farmData, BigInt('0')],
        account: account as Address,
      });

      const minAmountOut = toBN(minAmountOutRequest.result[0])
        .times(DEFAULT_SLIPPAGE_PARAM)
        .toFixed(0);

      writeContract({
        abi,
        address: vault.address as Address,
        functionName: 'updateFarms',
        chainId,
        args: [farmData, BigInt(minAmountOut)],
      });
    },
    [chainId, writeContract, account, vault],
  );

  return {
    writeContract: onWriteContract,
    ...rest,
  };
};

export const useEditProfitPercent = (
  vault: VaultDto,
  handleUpdate: () => Promise<void>,
) => {
  const { chainId, account } = useAuth();

  const { mutateAsync: updateVault } = useUpdateVaultData();

  const { writeContract, ...rest } = useWriteContractExtraGas({
    config,
    mutation: {
      onSuccess: async (data) => {
        const transactionReceipt = await waitForTransactionReceipt(config, {
          hash: data,
        });

        await updateVault({ vaultId: vault.id, account: account as Address });
        await handleUpdate();

        showToast(
          {
            title: SuccessMessages.Updated,
            withLink: {
              hash: transactionReceipt.transactionHash,
              chainId: chainId as number,
            },
          },
          ToastType.Success,
        );
      },
      onError: (e) => {
        console.log('useEditProfitPercent:', e);
        showToast({ title: mapWagmiError(e) }, ToastType.Error);
      },
    },
  });

  const onWriteContract = useCallback(
    (takeProfitPercent: number) => {
      const currentTakeProfitPercent = applyDecimals(
        takeProfitPercent,
        DEFAULT_VAULT_DIVIDER,
      )
        .div(PERCENT_DIVIDER)
        .toFixed(0);

      writeContract({
        abi,
        address: vault.address as Address,
        functionName: 'updateProfit',
        chainId,
        args: [BigInt(currentTakeProfitPercent)],
      });
    },
    [chainId, writeContract, vault],
  );

  return {
    writeContract: onWriteContract,
    ...rest,
  };
};

export const useEditProfitToken = (
  vault: VaultDto,
  handleUpdate: () => Promise<void>,
) => {
  const { chainId, account } = useAuth();
  const { mutateAsync: updateVault } = useUpdateVaultData();

  const { writeContract, ...rest } = useWriteContractExtraGas({
    config,
    mutation: {
      onSuccess: async (data) => {
        const transactionReceipt = await waitForTransactionReceipt(config, {
          hash: data,
        });

        await updateVault({ vaultId: vault.id, account: account as Address });
        await handleUpdate();

        showToast(
          {
            title: SuccessMessages.Updated,
            withLink: {
              hash: transactionReceipt.transactionHash,
              chainId: chainId as number,
            },
          },
          ToastType.Success,
        );
      },
      onError: (e) => {
        console.log('useEditProfitToken:', e);
        showToast({ title: mapWagmiError(e) }, ToastType.Error);
      },
    },
  });

  const onWriteContract = useCallback(
    (takeProfitToken: Address) => {
      writeContract({
        abi,
        address: vault.address as Address,
        functionName: 'updateProfitToken',
        chainId,
        args: [takeProfitToken],
      });
    },
    [chainId, writeContract, vault],
  );

  return {
    writeContract: onWriteContract,
    ...rest,
  };
};

export const useLiquidityInNative = ({
  vault,
  farms,
  isDisabled,
}: {
  vault: VaultDto;
  farms: FarmCard[];
  isDisabled: boolean;
}) => {
  const { account } = useAuth();

  const farmData = farms.map((item) => ({
    beacon: item.farm.beacon as Address,
    percent: BigInt(
      applyDecimals(item.percent, DEFAULT_VAULT_DIVIDER)
        .div(PERCENT_DIVIDER)
        .toFixed(0),
    ),
    data: '0x' as Address,
  }));

  const { data, isPending } = useSimulateContract({
    abi,
    address: vault.address as Address,
    functionName: 'updateFarms',
    args: [farmData, BigInt('0')],
    chainId: vault.chainId,
    account,
    query: {
      enabled: !!farms.length || !isDisabled,
    },
  });

  const liquidityInNative = useMemo(() => {
    if (!data?.result?.length) return toBN(0);
    return toBN(data?.result[1]);
  }, [data]);

  return {
    liquidityInNative,
    isPending,
  };
};
